import React from 'react'
import classes from './ConfigurePermissions.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { Input, Switch, Form, FormInstance, Button, Tooltip, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import { QuestionCircleOutlined, SearchOutlined, CheckOutlined } from '@ant-design/icons';
import { IDataUserTable, IFolder, IGroup, IPermission } from 'interfaces';
import api from 'api';
import { setConfigurableRole, setEditingRoleName, setEditNameMode, setGroupChoosen, setGroups, setIsConfigureRolesOpen, setUserAndData } from 'store/slices/dataUsersSlice';
import { defaultGroups } from 'store/reducers/usersGroupCreator';
import { useDebounce } from 'use-debounce';
import { allPermissions } from 'helpers/createPermissionTableData';
import RowLoader from 'components/Sceletons/RowLoader';
import { setIsLoadingPermissions } from 'store/slices/dataPermissionsSlice';
import downloadCascadePermissionCases from 'components/PermissionsByFolder/cascadePermissionsRules/canDownload';
import { languagesValues } from 'routes/Main/Main';
import { handleInputChangeWithAltFilter, handlePasteWithAltFilter } from 'helpers/filterAltCodes';

type ConfigurePermissionsType = {
  form: FormInstance<any>;
  currentPermissions: string[];
  setCurrentPermissions: React.Dispatch<React.SetStateAction<string[]>>;
  setIsUpdatingRole: React.Dispatch<React.SetStateAction<boolean>>;
  isUpdatingRole: boolean;
  extraAction?: () => void;
  setIsTheSameFoldersPermissions?: React.Dispatch<React.SetStateAction<boolean>>;
  isTheSameFoldersPermissions?: boolean;
};

export default function ConfigurePermissions({
  form,
  currentPermissions,
  setCurrentPermissions,
  setIsUpdatingRole,
  isUpdatingRole,
  extraAction,
  setIsTheSameFoldersPermissions,
  isTheSameFoldersPermissions,
}: ConfigurePermissionsType) {
  const [displayingPermissions, setDisplayingPermissions] = React.useState<IPermission[]>([]);
  const [searchText, setSearchText] = React.useState<string>('');
  const [hideReset, setHideReset] = React.useState(true);
  const [defaultFoldersPermissions, setDefaultFoldersPermissions] = React.useState<IFolder[]>([]);
  const [foldersPermissionsOfRole, setFoldersPermissionsOfRole] = React.useState<IFolder[]>([]);
  const [roleNameValue, setRoleNameValue] = React.useState<string>('');
  
  const { rolesPermissions, isGroupsAndUsersLoading, groups, dataUserTable, users,
    configurableRole, editingRoleName, editNameMode } = useAppSelector((state) => state.dataUsers);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { isLoadingPermissions } = useAppSelector((store) => store.permissions);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const isDefaultGroup = configurableRole ? defaultGroups.includes(configurableRole?.name) : false;
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const updateExistingRole = async () => {
    setIsUpdatingRole(true);
    await sendFoldersPermissions(configurableRole as IGroup);
    setDefaultFoldersPermissions(foldersPermissionsOfRole);
    setIsTheSameFoldersPermissions && setIsTheSameFoldersPermissions(true);
    dispatch(setIsConfigureRolesOpen(false));
    extraAction && extraAction();
    setIsUpdatingRole(false);
  };

  const existGroupNames = groups.map(group => group.group_name?.toLocaleLowerCase());
  const reservedNames = languagesValues.reduce((acc: string[], ln: {value: string, label: any}) => {    
    return [
      ...acc,
      t(`Users.table.user`, { lng: String(ln.value) }).toLocaleLowerCase(),
      t(`Users.table.full_administrator`, { lng: String(ln.value) }).toLocaleLowerCase()
    ]
  },[]);

  React.useEffect(() => {
    setDisplayingPermissions(rolesPermissions);
  },[rolesPermissions]);

  React.useEffect(() => {
    if (isUpdatingRole && !isTheSameFoldersPermissions && configurableRole?.id !== 'new') {
      updateExistingRole();
    };
    return () => {
      setRoleNameValue('');
    }
  },[isUpdatingRole, isTheSameFoldersPermissions, configurableRole]);

  React.useEffect(() => {
    getGroupPermissions();
  },[dataRoom]);

  React.useEffect(() => {
    if (configurableRole?.id === 'new') {
      setDisplayingPermissions(rolesPermissions);
      setSearchText('')
    }
  },[configurableRole]);

  React.useEffect(() => {
    setHideReset(hideButton())
  },[currentPermissions, foldersPermissionsOfRole]);

  React.useEffect(() => {
    if (debouncedSearchText) {
      const filteredPermissions = rolesPermissions.filter(permission => {
        const nameContainString = permission.name.toLocaleLowerCase().includes(debouncedSearchText.toLocaleLowerCase());
        const descriptionContainString = permission.description.toLocaleLowerCase().includes(debouncedSearchText.toLocaleLowerCase())
        return descriptionContainString || nameContainString;
      })
      setDisplayingPermissions(filteredPermissions);
    } else {
      setDisplayingPermissions(rolesPermissions);
    }
  }, [debouncedSearchText]);

  const getGroupPermissions = async () => {
    dispatch(setIsLoadingPermissions(true));    
    try {
      const { data: foldersPermissionsReq } = await api.getFoldersPermissions({room_id: dataRoom?.id!, group_id: configurableRole?.id === 'new' ? undefined : configurableRole?.id});
      const emptyPermissions = foldersPermissionsReq.map(folder => ({
        ...folder,
        permissions: configurableRole?.id === 'new' ? [] : folder.permissions,
      }));
        
      setDefaultFoldersPermissions(emptyPermissions);
      setFoldersPermissionsOfRole(emptyPermissions);
    } catch (e) {
      messageApi?.error(t('Documents.error.gettingPermissions'));
    } finally {
      dispatch(setIsLoadingPermissions(false));
    }
  };

  const onChange = (checked: boolean, _: any, permissionType: string) => {
    const newPermissions = checked
      ? [...currentPermissions, permissionType]
      : currentPermissions.filter(permission => permission !== permissionType);
    setCurrentPermissions(newPermissions);
  };

  const onChangeFolderPermisssions = (checked: boolean, _: any, folder_id: string, permission: string) => {
    setFoldersPermissionsOfRole(prev => prev.map(folder => {

      const newPermissions = checked
        ? [...folder.permissions || [], permission]
        : folder.permissions?.filter(current => current !== permission) || []
    
      const isCanView = newPermissions.length !== 0;
      const isCanDownload = downloadCascadePermissionCases
        .map((permissions) => permissions.every((el) => newPermissions.includes(el)))
        .includes(true);
      isCanView && newPermissions.push('can_view');
      isCanDownload && newPermissions.push('can_download');
      Boolean(newPermissions.includes('can_download') || isCanDownload)
        && newPermissions.push('can_download_pdf');

      const unicPermissions = (Array.from(new Set(newPermissions)));

      return folder.id === folder_id
      ? {...folder, permissions: unicPermissions}
      : folder
    }));
  };
  
  const sendFoldersPermissions = async (group: IGroup) => {
    let arrFolderNames: any[] = [];    
    await Promise.all(foldersPermissionsOfRole.map((folder) => {
      const currentFolderId = folder.id ? folder.id : null;
      const sortedCurrent = folder.permissions || [];
      const sortedCurrentPermissions = sortedCurrent.sort().join(',');
      const defaultPermissions = defaultFoldersPermissions.find(f => f.id === folder.id)?.permissions || [];
      const sortedDefaultPermissions = defaultPermissions.sort().join(',');      

      return sortedDefaultPermissions !== sortedCurrentPermissions && api.setFolderPermission({
        permission_types: folder.permissions as string[],
        folder_id: currentFolderId!,
        room_id: dataRoom?.id!,
        group_id: group?.id!,
        details: {
          before: defaultPermissions,
          after: folder.permissions,
          folder_id: folder.id,
          folder_name: folder.name,
          group_name: group?.name,
          group_role: group?.role?.name || '-',
          group_id: group?.id,
        }
      })
      .then(value => {
        if (Number(folder.id) === 0) arrFolderNames.push(0);
        else arrFolderNames.push(folder.name);
      })
      .catch(e => {
        messageApi?.warning(`${t('Documents.error.permissionsFailed')}' ${dataRoom?.folders.find(someFolder => folder.id === someFolder.id)?.name || 'ROOT_FOLDER'}'`);
      })
    }));
    const isNeedСomma = arrFolderNames.includes(0) ? ',' : '';
    const allNames = arrFolderNames.filter(name => name);
    const resetFolders = allNames.length > 0 ? isNeedСomma + t('Documents.success.andFolders') + allNames.join(', ') : '';
    messageApi?.success(`
      ${t('Documents.success.permissionsSuccess')}
      ${arrFolderNames.includes(0) ? t('Documents.management.rootDirectoty') : ''}${resetFolders}
    `, 10)
  };

  const onSubmit = async (values: any) => {
    setIsUpdatingRole(true);
    try {
      const newRole = await api.createRole({
        name: values.role_name,
        room_id: dataRoom?.id,
        permissions: currentPermissions,
      })

      const newGroup: IDataUserTable = {
        name: newRole.data.name,
        group_name: newRole.data.name,
        group_id: newRole.data.groups![0].id,
        id: newRole.data.groups![0].id,
        key: newRole.data.groups![0].id,
        first_name: `${newRole.data.groups![0].name} (0)`,
        type: 'group',
        emailOrRole: newRole.data.name,
        role: {
          id: newRole.data.id!,
          name: newRole.data.name,
          permissions: currentPermissions,
        },
        children: [],
      };      
      dispatch(setGroupChoosen(newGroup));
      dispatch(setConfigurableRole(newGroup));
      form.resetFields();
      dispatch(setGroups([...groups, newGroup]));
      dispatch(setUserAndData({ dataUserTable: [...dataUserTable, newGroup], users: users }));
      messageApi?.success(`${t('Users.modals.configureRoles.successCreateNewRole')} '${newRole.data.name}'`, 5);
    } catch (e: any) {
      messageApi?.error(t('Users.modals.configureRoles.errorCreateNewRole'))
    } finally {
      isTheSameFoldersPermissions && setIsUpdatingRole(false);
      isTheSameFoldersPermissions && extraAction && extraAction();
      isTheSameFoldersPermissions && dispatch(setIsConfigureRolesOpen(false));
    }
  };  

  const hideButton = () => {
    const sortedRolePermissions = configurableRole?.role?.permissions && [...configurableRole?.role?.permissions].sort().join(',');
    const isTheSameRolePermissions = [...currentPermissions].sort().join(',') === sortedRolePermissions;
    
    const comparePremissions = foldersPermissionsOfRole.map(currFolder => {      
      const currentPermissions = currFolder.permissions || [];
      const sortedCurrentPermissions = currentPermissions.sort().join(',');
      const defaultPermissions = defaultFoldersPermissions.find(f => f.id === currFolder.id)?.permissions || [];
      const sortedDefaultPermissions = defaultPermissions.sort().join(',');
      return sortedDefaultPermissions === sortedCurrentPermissions;
    });

    if (comparePremissions.includes(false)) setIsTheSameFoldersPermissions && setIsTheSameFoldersPermissions(false)
    else setIsTheSameFoldersPermissions && setIsTheSameFoldersPermissions(true);

    return (configurableRole?.id === 'new' || isTheSameRolePermissions)
      ? true
      : false
  };

  const onRoleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChangeWithAltFilter(event, (filteredValue) => {
      setRoleNameValue(filteredValue);
      form.setFieldValue('role_name', filteredValue);
    });
  };

  const onRoleNamePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    handlePasteWithAltFilter(event, roleNameValue, (filteredValue) => {
      setRoleNameValue(filteredValue);
      form.setFieldValue('role_name', filteredValue);
    });
  };
  
  return (
    <div className={classes.configureSection}>
      { configurableRole?.id === 'new' || (currentPermissions && !isGroupsAndUsersLoading && Boolean(configurableRole?.id))
        ? (<>
          <div className={classes.inputsWrap}>
            {
              configurableRole?.id === 'new'
                ? (
                  <Form onFinish={onSubmit} layout='vertical' form={form}>
                    <Form.Item
                      name='role_name'
                      label={t('Users.modals.configureRoles.roleName')}
                      rules={[
                        { required: true, whitespace: true, message: t('Users.modals.configureRoles.enterRoleName') },
                        { validator: (_, value) =>{
                          if (!value) {
                            return Promise.resolve()
                          }
                          if (reservedNames.includes(value.toLocaleLowerCase())) {
                            return Promise.reject(new Error(t('Users.modals.configureRoles.name_reserved')))
                          }
                          if (existGroupNames.includes(value.toLocaleLowerCase())) {
                            return Promise.reject(new Error(t('Users.modals.configureRoles.name_exists')))
                          }
                          return Promise.resolve()
                        }
                        },
                      ]}
                    >
                      <Input 
                        showCount 
                        maxLength={20} 
                        placeholder={t('Users.modals.configureRoles.nameNewRole')}
                        value={roleNameValue}
                        onChange={onRoleNameChange}
                        onPaste={onRoleNamePaste}
                      />
                    </Form.Item>
                  </Form>
                )                
                : (
                  <div className={classes.input}>
                    {editNameMode
                      ? <Input
                        onChange={(e) => dispatch(setEditingRoleName(e.target.value))}
                        value={editingRoleName}
                        showCount
                        maxLength={20}
                        suffix={
                          <Tooltip title={t('Users.modals.configureRoles.renameRole')}>
                            <Button
                              onClick={() => {
                                dispatch(setEditNameMode(!editNameMode));
                                dispatch(setConfigurableRole({ ...configurableRole!, name: editingRoleName }));       
                              }}
                              className={classes.searchBtn}
                              icon={<CheckOutlined style={{color: 'white'}}/>}
                              size='small'
                              type='primary'
                            />
                          </Tooltip>
                        }
                      /> : <Input
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={t('Users.modals.configureRoles.searchPermission')}
                        suffix={<SearchOutlined style={{color: 'gray', paddingRight: 4}}/>}
                        allowClear
                      />
                    }
                  </div>
                )
            }
          </div>
          <div className={classes.configureTitle}>
            <div>{t('Users.modals.configureRoles.configure')}</div>
            <Button
              onClick={() => setCurrentPermissions(configurableRole?.role?.permissions)}
              className={hideReset ? classes.hideButton : classes.resetButton}
              type='link'
            >
              {t('Users.modals.configureRoles.reset')}
            </Button>
          </div>
          <div className={classes.permissionsWrap} style={{height: `calc(100% - 111px`}}>
            {displayingPermissions[0]
              ? <Collapse
                  expandIconPlacement={'end'}
                  bordered={false}
                  defaultActiveKey={['general']}
                  size='small'
                  items={[{
                    key: 'general',
                    label: t('Users.modals.invite.general'),
                    extra: `${currentPermissions?.filter((permission) => permission !== 'can_edit_user_group').length}/${rolesPermissions?.length}`,
                    children: displayingPermissions.map(el => (
                      <div className={classes.permissionItem} key={el.id}>
                        <div className={classes.permissionTitle}>
                          <span>{t(`Users.modals.permissions.titles.${el.type}`)}</span>
                          <Tooltip title={t(`Users.modals.permissions.descriptions.${el.type}`)}>
                            <QuestionCircleOutlined className={classes.permissionDescription}/>
                          </Tooltip>
                        </div>
                        <Switch
                          size='small'
                          disabled={configurableRole ? isDefaultGroup : false}
                          checked={currentPermissions?.includes(el.type)}
                          onChange={(checked, event) => onChange(checked, event, el.type)}
                        />
                      </div>
                    )),
                  }]}
                />
              : <div className={classes.noFound}>{t(`Users.modals.configureRoles.noFound`)}</div>
            }
            
            <div className={classes.configureTitle}>{t('Users.modals.invite.foldersPermissions')}</div>
            {
              isLoadingPermissions 
                ? <RowLoader width='100%' padding='0 0 0 0' height='80px' />
                : foldersPermissionsOfRole?.map((folder: any) => (
                  <Collapse
                    className={classes.folderPermissions}
                    key={folder.id}
                    expandIconPlacement={'end'}
                    bordered={false}
                    size='small'
                    items={[{
                      key: folder.id,
                      label: <div className={classes.folderName}>{folder.id === 0 ? t('Documents.modals.rootDir') : folder.name}</div>,
                      extra: `${folder?.permissions?.length || 0}/${allPermissions?.length}`,
                      children: allPermissions?.map((permission) => (
                        <div key={permission} className={classes.permissionItem}>
                          <span className={classes.permissionTitle}>{t(`Logs.logsModal.${permission}`)}</span>
                          <Switch
                            size='small'
                            disabled={configurableRole?.name === 'full_administrator'}
                            checked={folder?.permissions?.includes(permission)}
                            onChange={(checked, event) => onChangeFolderPermisssions(checked, event, folder.id, permission)}
                          />
                        </div>
                      )),
                    }]}
                  />
                ))
            }
          </div>
        </>)
        : (
          <div className={classes.chooseSomeGroup}>
            <div>{t('Users.modals.configureRoles.chooseRole')}</div>
          </div>
        )
      }
    </div>
  )
}
